import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import UserIdentityLabel from "./components/UserIdentityLabel";
import { useAuth } from "./authContext";
import { useCurrentUser } from "./currentUserContext";
import { useNotifications } from "./useNotifications";

function formatNotificationTimestamp(value) {
  const timestamp = typeof value === "number" ? new Date(value) : new Date(value || "");

  if (Number.isNaN(timestamp.getTime())) {
    return "";
  }

  return timestamp.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

export default function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, isLoading } = useAuth();
  const { selectedUserId } = useCurrentUser();
  const isStaffUser = user?.role === "admin" || user?.role === "moderator";
  const navRef = useRef(null);
  const [isBrowseMenuOpen, setIsBrowseMenuOpen] = useState(false);
  const [isNotificationsMenuOpen, setIsNotificationsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const browseMenuRef = useRef(null);
  const notificationsMenuRef = useRef(null);
  const userMenuRef = useRef(null);
  const mobileBrowseMenuRef = useRef(null);
  const mobileNotificationsMenuRef = useRef(null);
  const mobileUserMenuRef = useRef(null);
  const {
    notificationItems,
    notificationsLoading,
    hasNotifications,
    hasUnreadNotifications,
    notificationsCount,
    markNotificationSeen
  } = useNotifications({ isAuthenticated, selectedUserId });

  const closeAllMenus = () => {
    setIsBrowseMenuOpen(false);
    setIsNotificationsMenuOpen(false);
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      closeAllMenus();
      navigate("/");
    } catch (error) {
      console.error("Navigation logout error:", error);
    }
  };

  const getNavLinkClassName = ({ isActive }) => `app-nav__link${isActive ? " app-nav__link--active" : ""}`;
  const getMobileNavLinkClassName = ({ isActive }) => `app-nav__mobile-link${isActive ? " app-nav__mobile-link--active" : ""}`;
  const isBrowseMenuActive = location.pathname.startsWith("/browse");
  const isUserMenuActive = location.pathname === "/account" || location.pathname === "/account/messages";
  const isNotificationsMenuActive = location.pathname === "/account/messages";

  useEffect(() => {
    closeAllMenus();
  }, [location.pathname]);

  useEffect(() => {
    if (!isUserMenuOpen && !isBrowseMenuOpen && !isNotificationsMenuOpen && !isMobileMenuOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (isMobileMenuOpen && !navRef.current?.contains(event.target)) {
        closeAllMenus();
        return;
      }

      if (!browseMenuRef.current?.contains(event.target) && !mobileBrowseMenuRef.current?.contains(event.target)) {
        setIsBrowseMenuOpen(false);
      }

      if (!notificationsMenuRef.current?.contains(event.target) && !mobileNotificationsMenuRef.current?.contains(event.target)) {
        setIsNotificationsMenuOpen(false);
      }

      if (!userMenuRef.current?.contains(event.target) && !mobileUserMenuRef.current?.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeAllMenus();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isBrowseMenuOpen, isMobileMenuOpen, isNotificationsMenuOpen, isUserMenuOpen]);

  const handleNavigateWithClose = (path) => {
    closeAllMenus();
    navigate(path);
  };

  const handleNotificationCardClick = (event, item) => {
    const path = item?.primaryPath;

    if (!path) {
      return;
    }

    if (event.target.closest("a, button")) {
      return;
    }

    markNotificationSeen(item);
    handleNavigateWithClose(path);
  };

  const handleNotificationCardKeyDown = (event, item) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    markNotificationSeen(item);
    handleNavigateWithClose(item?.primaryPath);
  };

  useEffect(() => {
    if (!isMobileMenuOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen]);

  return (
    <nav className="app-nav" ref={navRef}>
      <div className="app-nav__mobile-bar">
        <NavLink
          to="/"
          className="app-nav__brand"
          end
          onClick={closeAllMenus}
        >
          MyTradeFinder
        </NavLink>
        <button
          type="button"
          className={`app-nav__mobile-toggle${isMobileMenuOpen ? " app-nav__mobile-toggle--active" : ""}`}
          aria-expanded={isMobileMenuOpen ? "true" : "false"}
          aria-controls="app-nav-mobile-panel"
          aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          onClick={() => {
            setIsMobileMenuOpen((currentValue) => !currentValue);
            setIsBrowseMenuOpen(false);
            setIsUserMenuOpen(false);
          }}
        >
          <span className="app-nav__mobile-toggle-icon" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          <span>{isMobileMenuOpen ? "Close" : "Menu"}</span>
        </button>
      </div>

      <div className="app-nav__inner app-nav__inner--desktop">
        <div className="app-nav__primary">
          <NavLink to="/" className={getNavLinkClassName} end>Home</NavLink>
          <div className="app-nav__menu app-nav__menu--browse" ref={browseMenuRef}>
            <button
              type="button"
              className={`app-nav__button app-nav__menu-trigger${isBrowseMenuActive ? " app-nav__menu-trigger--active" : ""}`}
              aria-haspopup="menu"
              aria-expanded={isBrowseMenuOpen ? "true" : "false"}
              onClick={() => {
                setIsBrowseMenuOpen((currentValue) => !currentValue);
                setIsUserMenuOpen(false);
              }}
            >
              <span>Browse</span>
              <span className="app-nav__menu-caret" aria-hidden="true">▾</span>
            </button>
            {isBrowseMenuOpen ? (
              <div className="app-nav__menu-popover" role="menu">
                <NavLink to="/browse/cards" className="app-nav__menu-item" role="menuitem">Browse cards</NavLink>
                <NavLink to="/browse/users" className="app-nav__menu-item" role="menuitem">Browse users</NavLink>
              </div>
            ) : null}
          </div>
          <NavLink to="/tradefinder" className={getNavLinkClassName}>TradeFinder</NavLink>
          <NavLink to="/traders" className={getNavLinkClassName}>My Traders</NavLink>
          <NavLink to="/wants" className={getNavLinkClassName}>My Wants</NavLink>
          {isAuthenticated && isStaffUser ? <NavLink to="/admin" className={getNavLinkClassName}>Admin</NavLink> : null}
        </div>
        <div className="app-nav__secondary">
        {isAuthenticated ? (
          <>
            <div className="app-nav__menu" ref={notificationsMenuRef}>
              <button
                type="button"
                className={`app-nav__button app-nav__menu-trigger${isNotificationsMenuActive ? " app-nav__menu-trigger--active" : ""}`}
                aria-haspopup="menu"
                aria-expanded={isNotificationsMenuOpen ? "true" : "false"}
                onClick={() => {
                  setIsNotificationsMenuOpen((currentValue) => !currentValue);
                  setIsBrowseMenuOpen(false);
                  setIsUserMenuOpen(false);
                }}
              >
                <span>Notifications</span>
                {hasNotifications ? (
                  <span className={`app-nav__notification-badge${hasUnreadNotifications ? "" : " app-nav__notification-badge--seen"}`}>
                    <span className="app-nav__notification-badge-value">{notificationsCount}</span>
                  </span>
                ) : null}
                <span className="app-nav__menu-caret" aria-hidden="true">▾</span>
              </button>
              {isNotificationsMenuOpen ? (
                <div className="app-nav__menu-popover app-nav__menu-popover--notifications" role="menu">
                  {notificationsLoading ? (
                    <div className="app-nav__notifications-state">Loading notifications...</div>
                  ) : null}

                  {!notificationsLoading && hasNotifications ? (
                    <div className="app-nav__notifications-list">
                      {notificationItems.map((item) => (
                        <div
                          key={`notification-${item.counterparty.id}`}
                          className={`app-nav__notification-card${item.hasUnreadMessage ? " app-nav__notification-card--message" : ""}${item.isUnread ? " app-nav__notification-card--unread" : " app-nav__notification-card--read"}`}
                          role="button"
                          tabIndex={0}
                          onClick={(event) => handleNotificationCardClick(event, item)}
                          onKeyDown={(event) => handleNotificationCardKeyDown(event, item)}
                        >
                          <div className="app-nav__notification-copy">
                            <div className="app-nav__notification-header-row">
                              <Link
                                to={item.browsePath}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  markNotificationSeen(item);
                                  closeAllMenus();
                                }}
                                className="app-nav__notification-userlink"
                              >
                                <UserIdentityLabel
                                  username={item.counterparty.username || `User ${item.counterparty.id}`}
                                  tier={item.counterparty.collectorTier}
                                  role={item.counterparty.role || "collector"}
                                  size="sm"
                                  color="inherit"
                                  truncateAt={10}
                                />
                              </Link>
                            </div>
                            {item.isUnread ? <span className="app-nav__notification-unread-dot" aria-label="New notification" /> : null}
                            <div className="app-nav__notification-status app-nav__notification-status--message">{item.notificationText}</div>
                            {item.lastActivityAt ? (
                              <div className="app-nav__notification-meta" style={{ fontSize: "10px", color: "#64748b", marginTop: "4px" }}>
                                {formatNotificationTimestamp(item.lastActivityAt)}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {!notificationsLoading && !hasNotifications ? (
                    <div className="app-nav__notifications-empty">
                      No new messages or trade proposals are waiting right now.
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="app-nav__menu" ref={userMenuRef}>
              <button
                type="button"
                className={`app-nav__button app-nav__menu-trigger${isUserMenuActive ? " app-nav__menu-trigger--active" : ""}`}
                aria-haspopup="menu"
                aria-expanded={isUserMenuOpen ? "true" : "false"}
                onClick={() => {
                  setIsUserMenuOpen((currentValue) => !currentValue);
                  setIsBrowseMenuOpen(false);
                  setIsNotificationsMenuOpen(false);
                }}
              >
                <UserIdentityLabel username={user?.username || "User"} tier={user?.collectorTier} role={user?.role} size="sm" truncateAt={10} underline={false} />
                <span className="app-nav__menu-caret" aria-hidden="true">▾</span>
              </button>
              {isUserMenuOpen ? (
                <div className="app-nav__menu-popover" role="menu">
                  <NavLink to="/account" className="app-nav__menu-item" role="menuitem" end>My Account</NavLink>
                  <NavLink to="/account/messages" className="app-nav__menu-item" role="menuitem">My Messages</NavLink>
                  <button
                    type="button"
                    onClick={handleLogout}
                    disabled={isLoading}
                    className="app-nav__menu-item app-nav__menu-item--danger"
                    role="menuitem"
                  >
                    Log out
                  </button>
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <NavLink to="/auth" className={getNavLinkClassName}>Log in</NavLink>
          </>
        )}
        </div>
      </div>

      {isMobileMenuOpen ? (
        <div id="app-nav-mobile-panel" className="app-nav__mobile-panel">
          <div className="app-nav__mobile-group">
            <div className="app-nav__mobile-group-label">Navigate</div>
            <NavLink to="/" className={getMobileNavLinkClassName} end onClick={closeAllMenus}>Home</NavLink>
            <div ref={mobileBrowseMenuRef}>
              <button
                type="button"
                className={`app-nav__mobile-section-toggle${isBrowseMenuActive ? " app-nav__mobile-section-toggle--active" : ""}`}
                aria-expanded={isBrowseMenuOpen ? "true" : "false"}
                onClick={() => {
                  setIsBrowseMenuOpen((currentValue) => !currentValue);
                  setIsNotificationsMenuOpen(false);
                  setIsUserMenuOpen(false);
                }}
              >
                <span>Browse</span>
                <span className="app-nav__menu-caret" aria-hidden="true">▾</span>
              </button>
              {isBrowseMenuOpen ? (
                <div className="app-nav__mobile-submenu">
                  <NavLink to="/browse/cards" className={getMobileNavLinkClassName} onClick={closeAllMenus}>Browse cards</NavLink>
                  <NavLink to="/browse/users" className={getMobileNavLinkClassName} onClick={closeAllMenus}>Browse users</NavLink>
                </div>
              ) : null}
            </div>
            <NavLink to="/tradefinder" className={getMobileNavLinkClassName} onClick={closeAllMenus}>TradeFinder</NavLink>
            <NavLink to="/traders" className={getMobileNavLinkClassName} onClick={closeAllMenus}>My Traders</NavLink>
            <NavLink to="/wants" className={getMobileNavLinkClassName} onClick={closeAllMenus}>My Wants</NavLink>
            {isAuthenticated && isStaffUser ? <NavLink to="/admin" className={getMobileNavLinkClassName} onClick={closeAllMenus}>Admin</NavLink> : null}
          </div>

          {isAuthenticated ? (
            <div className="app-nav__mobile-group">
              <div className="app-nav__mobile-group-label">Account</div>
              <div ref={mobileNotificationsMenuRef}>
                <button
                  type="button"
                  className={`app-nav__mobile-section-toggle${isNotificationsMenuActive ? " app-nav__mobile-section-toggle--active" : ""}`}
                  aria-expanded={isNotificationsMenuOpen ? "true" : "false"}
                  onClick={() => {
                    setIsNotificationsMenuOpen((currentValue) => !currentValue);
                    setIsBrowseMenuOpen(false);
                    setIsUserMenuOpen(false);
                  }}
                >
                  <span>Notifications</span>
                  <span className="app-nav__mobile-section-toggle-meta">
                    {hasNotifications ? (
                      <span className={`app-nav__notification-badge${hasUnreadNotifications ? "" : " app-nav__notification-badge--seen"}`}>
                        <span className="app-nav__notification-badge-value">{notificationsCount}</span>
                      </span>
                    ) : null}
                    <span className="app-nav__menu-caret" aria-hidden="true">▾</span>
                  </span>
                </button>
                {isNotificationsMenuOpen ? (
                  <div className="app-nav__mobile-submenu app-nav__mobile-submenu--notifications">
                    {notificationsLoading ? <div className="app-nav__notifications-state">Loading notifications...</div> : null}
                    {!notificationsLoading && hasNotifications ? notificationItems.map((item) => (
                      <div
                        key={`mobile-notification-${item.counterparty.id}`}
                        className={`app-nav__mobile-notification-card${item.isUnread ? " app-nav__mobile-notification-card--unread" : " app-nav__mobile-notification-card--read"}`}
                        role="button"
                        tabIndex={0}
                        onClick={(event) => handleNotificationCardClick(event, item)}
                        onKeyDown={(event) => handleNotificationCardKeyDown(event, item)}
                      >
                        <div className="app-nav__notification-header-row">
                          <Link
                            to={item.browsePath}
                            onClick={(event) => {
                              event.stopPropagation();
                              markNotificationSeen(item);
                              closeAllMenus();
                            }}
                            className="app-nav__notification-userlink app-nav__notification-userlink--mobile"
                          >
                            <UserIdentityLabel
                              username={item.counterparty.username || `User ${item.counterparty.id}`}
                              tier={item.counterparty.collectorTier}
                              role={item.counterparty.role || "collector"}
                              size="sm"
                              color="inherit"
                              truncateAt={10}
                            />
                          </Link>
                        </div>
                        <div className="app-nav__notification-copy">
                          {item.isUnread ? <span className="app-nav__notification-unread-dot" aria-label="New notification" /> : null}
                          <span className="app-nav__notification-status app-nav__notification-status--message">{item.notificationText}</span>
                          {item.lastActivityAt ? (
                            <span className="app-nav__notification-meta" style={{ display: "block", fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
                              {formatNotificationTimestamp(item.lastActivityAt)}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    )) : null}
                    {!notificationsLoading && !hasNotifications ? (
                      <div className="app-nav__notifications-empty">No new messages or trade proposals are waiting right now.</div>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div ref={mobileUserMenuRef}>
                <button
                  type="button"
                  className={`app-nav__mobile-section-toggle${isUserMenuActive ? " app-nav__mobile-section-toggle--active" : ""}`}
                  aria-expanded={isUserMenuOpen ? "true" : "false"}
                  onClick={() => {
                    setIsUserMenuOpen((currentValue) => !currentValue);
                    setIsBrowseMenuOpen(false);
                    setIsNotificationsMenuOpen(false);
                  }}
                >
                  <UserIdentityLabel username={user?.username || "User"} tier={user?.collectorTier} role={user?.role} size="sm" truncateAt={10} />
                  <span className="app-nav__menu-caret" aria-hidden="true">▾</span>
                </button>
                {isUserMenuOpen ? (
                  <div className="app-nav__mobile-submenu">
                    <NavLink to="/account" className={getMobileNavLinkClassName} end onClick={closeAllMenus}>My Account</NavLink>
                    <NavLink to="/account/messages" className={getMobileNavLinkClassName} onClick={closeAllMenus}>My Messages</NavLink>
                    <button
                      type="button"
                      onClick={handleLogout}
                      disabled={isLoading}
                      className="app-nav__mobile-link app-nav__mobile-link--danger"
                    >
                      Log out
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="app-nav__mobile-group">
              <div className="app-nav__mobile-group-label">Account</div>
              <NavLink to="/auth" className={getMobileNavLinkClassName} onClick={closeAllMenus}>Log in</NavLink>
            </div>
          )}

        </div>
      ) : null}
    </nav>
  );
}
